import React, { useState, useRef, useCallback, useEffect } from 'react';

const A4_WIDTH = 595;
const A4_HEIGHT = 842;
const ZOOM_LEVELS = [0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];
const DEFAULT_ZOOM_INDEX = 3; // 0.7
const HANDLE_SIZE = 8;

const TemplatePreview = ({ editingFields, selectedFieldId, selectField, updateField }) => {
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragFieldStart, setDragFieldStart] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [dragFieldId, setDragFieldId] = useState(null);

  const containerRef = useRef(null);
  const pageRef = useRef(null);

  const zoom = ZOOM_LEVELS[zoomIndex];
  const pageWidth = A4_WIDTH * zoom;
  const pageHeight = A4_HEIGHT * zoom;

  const zoomIn = () => setZoomIndex(i => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  const zoomOut = () => setZoomIndex(i => Math.max(i - 1, 0));
  const zoomFit = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 40;
      const fitZoom = containerWidth / A4_WIDTH;
      const closest = ZOOM_LEVELS.reduce((prev, curr, idx) =>
        Math.abs(curr - fitZoom) < Math.abs(ZOOM_LEVELS[prev] - fitZoom) ? idx : prev, 0);
      setZoomIndex(closest);
    }
  };

  // Convert mouse position to PDF coordinates
  const mouseToPdf = useCallback((e) => {
    if (!pageRef.current) return { x: 0, y: 0 };
    const rect = pageRef.current.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - rect.left) / zoom),
      y: Math.round((e.clientY - rect.top) / zoom)
    };
  }, [zoom]);

  // Drag start
  const handleFieldMouseDown = useCallback((e, fieldId, handle = null) => {
    e.stopPropagation();
    e.preventDefault();

    const field = editingFields.find(f => f.id === fieldId);
    if (!field) return;

    selectField(fieldId);
    setDragFieldId(fieldId);
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragFieldStart({ x: field.x, y: field.y, w: field.width, h: field.height });

    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
    } else {
      setIsDragging(true);
    }
  }, [editingFields, selectField]);

  // Mouse move handler
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      const dx = (e.clientX - dragStart.x) / zoom;
      const dy = (e.clientY - dragStart.y) / zoom;

      if (isDragging && dragFieldId) {
        const newX = Math.max(0, Math.min(A4_WIDTH - dragFieldStart.w, Math.round(dragFieldStart.x + dx)));
        const newY = Math.max(0, Math.min(A4_HEIGHT - dragFieldStart.h, Math.round(dragFieldStart.y + dy)));
        updateField(dragFieldId, { x: newX, y: newY });
      }

      if (isResizing && dragFieldId) {
        let newX = dragFieldStart.x;
        let newY = dragFieldStart.y;
        let newW = dragFieldStart.w;
        let newH = dragFieldStart.h;

        if (resizeHandle.includes('e')) newW = Math.max(20, Math.round(dragFieldStart.w + dx));
        if (resizeHandle.includes('s')) newH = Math.max(10, Math.round(dragFieldStart.h + dy));
        if (resizeHandle.includes('w')) {
          newX = Math.max(0, Math.round(dragFieldStart.x + dx));
          newW = Math.max(20, Math.round(dragFieldStart.w - dx));
        }
        if (resizeHandle.includes('n')) {
          newY = Math.max(0, Math.round(dragFieldStart.y + dy));
          newH = Math.max(10, Math.round(dragFieldStart.h - dy));
        }

        // Clamp to page bounds
        if (newX + newW > A4_WIDTH) newW = A4_WIDTH - newX;
        if (newY + newH > A4_HEIGHT) newH = A4_HEIGHT - newY;

        updateField(dragFieldId, { x: newX, y: newY, width: newW, height: newH });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      setDragFieldId(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, dragFieldStart, dragFieldId, resizeHandle, zoom, updateField]);

  // Click on empty area deselects
  const handlePageClick = (e) => {
    if (e.target === pageRef.current) {
      selectField(null);
    }
  };

  // Render resize handles for selected field
  const renderHandles = (field) => {
    if (selectedFieldId !== field.id) return null;

    const handles = [
      { id: 'nw', x: 0, y: 0, cursor: 'nw-resize' },
      { id: 'ne', x: field.width, y: 0, cursor: 'ne-resize' },
      { id: 'sw', x: 0, y: field.height, cursor: 'sw-resize' },
      { id: 'se', x: field.width, y: field.height, cursor: 'se-resize' },
      { id: 'n', x: field.width / 2, y: 0, cursor: 'n-resize' },
      { id: 's', x: field.width / 2, y: field.height, cursor: 's-resize' },
      { id: 'w', x: 0, y: field.height / 2, cursor: 'w-resize' },
      { id: 'e', x: field.width, y: field.height / 2, cursor: 'e-resize' },
    ];

    return handles.map(h => (
      <div
        key={h.id}
        onMouseDown={(e) => handleFieldMouseDown(e, field.id, h.id)}
        style={{
          position: 'absolute',
          left: `${(h.x * zoom) - HANDLE_SIZE / 2}px`,
          top: `${(h.y * zoom) - HANDLE_SIZE / 2}px`,
          width: `${HANDLE_SIZE}px`,
          height: `${HANDLE_SIZE}px`,
          backgroundColor: '#3b82f6',
          border: '1px solid #fff',
          borderRadius: '2px',
          cursor: h.cursor,
          zIndex: 20,
        }}
      />
    ));
  };

  const getFieldTypeColor = (type) => {
    switch (type) {
      case 'table': return { border: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' };
      case 'title': return { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.08)' };
      case 'data': return { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' };
      default: return { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)' };
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        padding: '6px 10px',
        backgroundColor: 'var(--bg)',
        borderRadius: '6px',
        border: '1px solid var(--border)',
      }}>
        <h4 style={{ margin: 0, color: 'var(--text)', fontSize: '14px' }}>
          Podglad szablonu
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={zoomOut}
            disabled={zoomIndex === 0}
            style={{
              padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              opacity: zoomIndex === 0 ? 0.4 : 1,
            }}
            title="Pomniejsz"
          >-</button>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '40px', textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={zoomIndex === ZOOM_LEVELS.length - 1}
            style={{
              padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              opacity: zoomIndex === ZOOM_LEVELS.length - 1 ? 0.4 : 1,
            }}
            title="Powieksz"
          >+</button>
          <button
            onClick={zoomFit}
            style={{
              padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
            }}
            title="Dopasuj do szerokosci"
          >Dopasuj</button>
        </div>
      </div>

      {/* Preview container */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          border: '1px solid var(--border)',
          borderRadius: '6px',
          backgroundColor: '#e5e7eb',
          overflow: 'auto',
          padding: '20px',
          minHeight: '500px',
        }}
      >
        {/* A4 page */}
        <div
          ref={pageRef}
          onClick={handlePageClick}
          style={{
            width: `${pageWidth}px`,
            height: `${pageHeight}px`,
            margin: '0 auto',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
            position: 'relative',
            cursor: isDragging ? 'grabbing' : 'default',
            userSelect: 'none',
          }}
        >
          {/* Grid lines (subtle) */}
          <svg
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.15 }}
          >
            {/* Margin guides */}
            <rect
              x={50 * zoom} y={50 * zoom}
              width={(A4_WIDTH - 100) * zoom} height={(A4_HEIGHT - 100) * zoom}
              fill="none" stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="4 4"
            />
          </svg>

          {/* Fields */}
          {editingFields.map(field => {
            const isSelected = selectedFieldId === field.id;
            const colors = getFieldTypeColor(field.type);
            const fieldFontSize = Math.max((field.style?.fontSize || 12) * zoom, 6);

            return (
              <div
                key={field.id}
                onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                onClick={(e) => { e.stopPropagation(); selectField(field.id); }}
                style={{
                  position: 'absolute',
                  left: `${field.x * zoom}px`,
                  top: `${field.y * zoom}px`,
                  width: `${field.width * zoom}px`,
                  height: `${field.height * zoom}px`,
                  fontSize: `${fieldFontSize}px`,
                  fontWeight: field.style?.fontWeight || 'normal',
                  textAlign: field.style?.textAlign || 'left',
                  color: field.style?.color || '#000',
                  border: isSelected
                    ? '2px solid #3b82f6'
                    : `1px solid ${colors.border}`,
                  backgroundColor: isSelected
                    ? 'rgba(59, 130, 246, 0.12)'
                    : colors.bg,
                  borderRadius: '2px',
                  overflow: 'hidden',
                  cursor: isSelected ? 'grab' : 'pointer',
                  zIndex: isSelected ? 10 : 1,
                  lineHeight: 1.2,
                  padding: `${Math.max(1, 2 * zoom)}px`,
                  boxSizing: 'border-box',
                }}
                title={`${field.type}: ${field.content}\n(${field.x}, ${field.y}) ${field.width}x${field.height}`}
              >
                {/* Type label */}
                {zoom >= 0.5 && (
                  <span style={{
                    position: 'absolute',
                    top: '-1px',
                    right: '-1px',
                    fontSize: '8px',
                    backgroundColor: colors.border,
                    color: '#fff',
                    padding: '0 3px',
                    borderRadius: '0 2px 0 2px',
                    lineHeight: '14px',
                    opacity: isSelected ? 1 : 0.7,
                    pointerEvents: 'none',
                  }}>
                    {field.type}
                  </span>
                )}
                {field.content}

                {/* Resize handles */}
                {isSelected && renderHandles(field)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Status bar */}
      {selectedFieldId && (() => {
        const field = editingFields.find(f => f.id === selectedFieldId);
        if (!field) return null;
        return (
          <div style={{
            marginTop: '6px',
            padding: '4px 10px',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px',
            border: '1px solid var(--border)',
            fontSize: '11px',
            color: 'var(--text-secondary)',
            display: 'flex',
            gap: '12px',
          }}>
            <span>X: {field.x}</span>
            <span>Y: {field.y}</span>
            <span>W: {field.width}</span>
            <span>H: {field.height}</span>
            <span>Typ: {field.type}</span>
          </div>
        );
      })()}
    </div>
  );
};

export default TemplatePreview;
